import { Stack, Typography } from '@mui/material';
import { Base64 } from 'js-base64';
import { useEffect, useState } from 'react';
import { Error } from '@/components/Common/Error/Error';
import { environments } from '@/environments';
import {
  COOKIE_ACCESS_TOKEN,
  COOKIE_ID_TOKEN,
  TOKEN_EXPIRES_IN,
  getAccessToken,
  setAccessToken,
  setIDToken,
} from '@/helpers/cookie';
import { RoutesMapping } from '@/helpers/routing';
import { useExchangeAccessToken } from '@/hooks/use-callback';
import { useQuery } from '@/hooks/use-url';

const Callback = () => {
  const [error, setError] = useState<string>();

  const { data } = useExchangeAccessToken();

  const urlParams = useQuery();

  const urlError = urlParams.get('error') || '';
  const urlErrorDetails = urlParams.get('error_description') || '';

  const teodUrl: string = (() => {
    try {
      const stateParam = urlParams.get('state') || '';
      const state = JSON.parse(Base64.atob(stateParam));
      if (typeof state.cv === 'string') {
        // Restore PKCE sessionStorage so js-pkce can complete the token exchange
        // cross-domain (verifier was stored on the TEOD domain, not here on staging).
        sessionStorage.setItem('pkce_code_verifier', state.cv);
        sessionStorage.setItem('pkce_state', stateParam);
      }
      return typeof state.teodUrl === 'string' ? state.teodUrl : null;
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    if (urlError) {
      setError(urlError || urlErrorDetails);
    }
  }, [urlError, urlErrorDetails]);

  useEffect(() => {
    if (error || !data) return;

    const { access_token, expires_in } = data;
    const idToken =
      COOKIE_ID_TOKEN in data && typeof data.id_token === 'string'
        ? data.id_token
        : null;

    if (!access_token) return;

    if (teodUrl) {
      const params = new URLSearchParams({
        [COOKIE_ACCESS_TOKEN]: access_token,
        [TOKEN_EXPIRES_IN]: String(expires_in),
        ...(idToken && { [COOKIE_ID_TOKEN]: idToken }),
      });
      window.location.replace(`${teodUrl}${RoutesMapping.RELAY}#${params}`);
      return;
    }

    setAccessToken(access_token, {
      expires: `${expires_in}s`,
      domain: environments.cookieDomain,
    });

    if (idToken) {
      setIDToken(idToken, {
        expires: `${expires_in}s`,
        domain: environments.cookieDomain,
      });
    }

    const cookieToken = getAccessToken();

    if (cookieToken) {
      const currentLocation =
        localStorage.getItem('lastLocation') || environments.siteUrl;
      setTimeout(() => window.location.replace(currentLocation), 3000);
    }
  }, [error, data, teodUrl]);

  if (error) return <Error code={403} />;

  return (
    <Stack
      sx={{
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'center',
        py: 8,
      }}
    >
      <Typography variant="body2">Signing you in, please wait ...</Typography>
    </Stack>
  );
};

export default Callback;
